import React, {createContext, useState, useMemo, useContext} from "react";
import styled from "styled-components";
import noop from "lodash/noop";
import {Link} from "react-router-dom";
import MenuIcon from "react-icons/lib/io/android-menu";
import Button from "@unibuddy/machop/Experimental/General/components/Button/Button";
import Stack from "@unibuddy/machop/Experimental/Layout/components/Stack/Stack";
import Box from "@unibuddy/machop/Experimental/Layout/components/Box/Box";
import useAuth from "@unibuddy/machop/Auth/hooks/useAuth";
import Drawer, {
    DrawerScroll,
} from "@unibuddy/machop/Experimental/Overlays/components/Drawer/Drawer";
import {useLiveEvent} from "../../../core/components/LiveEventProvider/LiveEventProvider";
import LiveEventRealTimeStats from "../../../LiveEventStats/components/LiveEventRealTimeStats/LiveEventRealTimeStats";
import UserProfileLine from "../../../Auth/components/UserProfileLine/UserProfileLine";

export const Content = styled.div`
    height: calc(100% - 55px);
    position: relative;
    z-index: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    @media (min-width: 1120px) {
        height: calc(100% - 60px);
    }
`;

const Wrapper = styled.div`
    display: flex;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;

    & > * {
        min-width: 0;
    }
`;

const Main = styled.div`
    flex: 1;
    @media (min-width: 768px) {
        border-left: 1px solid var(--solitude);
        border-right: 1px solid var(--solitude);
    }
`;

const Logo = styled.img`
    display: block;
    height: 40px;
    max-width: 268px;
    padding: 0 16px;
`;

export const LayoutContext = createContext({isOpen: false, toggle: noop});

export const Container = ({children, ...props}) => {
    const [isOpen, setIsOpen] = useState(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const toggle = (value) => {
        if (value !== undefined) {
            setIsOpen(value);
        } else {
            setIsOpen((state) => !state);
        }
    };
    const value = useMemo(() => ({isOpen, toggle}), [isOpen, toggle]);
    return (
        <LayoutContext.Provider value={value}>
            <Wrapper {...props} onClick={() => toggle(false)}>
                {children}
            </Wrapper>
        </LayoutContext.Provider>
    );
};

export const Body = ({children, ...props}) => {
    return <Main {...props}>{children}</Main>;
};

export const LeftColumn = ({children, role}) => {
    const {isLoggedIn} = useAuth();
    const {university, liveEvent} = useLiveEvent();
    const {isOpen, toggle} = useContext(LayoutContext);
    return (
        <Drawer
            size={["normal", "normal", "small", "small", "normal"]}
            staticAbove="tablet"
            side="left"
            isOpen={isOpen}
            position="fixed"
            onDismiss={() => toggle(false)}
            id="main-menu"
            role={role}
            footer={
                isLoggedIn ? (
                    <Box padding="small">
                        <UserProfileLine />
                    </Box>
                ) : null
            }
        >
            <DrawerScroll>
                <Stack space="medium" dividers>
                    <Box data-test-id="university-logo" paddingTop="medium">
                        <Link to={`/${university.slug}/${liveEvent.slug}`}>
                            <Logo
                                src={university.logo}
                                alt={`${university.name} Logo, link to welcome page`}
                            />
                        </Link>
                        {liveEvent && (
                            <Box paddingX="medium">
                                <LiveEventRealTimeStats liveEventId={liveEvent.id} />
                            </Box>
                        )}
                    </Box>
                    {/* here we catch any click event that bubbles up and close the drawer */}
                    <div onClick={() => toggle(false)}>{children}</div>
                </Stack>
            </DrawerScroll>
        </Drawer>
    );
};

const HeaderContainer = styled.header`
    position: relative;
    padding-left: ${({paddingLeft}) => paddingLeft || "55px"};
    height: 55px;
    border-bottom: 4px solid var(--theme-primary);

    @media (min-width: 920px) {
        padding-left: 0;
    }
    @media (min-width: 1120px) {
        height: 60px;
    }
`;
const HeaderContent = styled.div`
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-right: 16px;

    @media (min-width: 920px) {
        padding-left: 16px;
    }

    & > * {
        min-width: 0;
    }
`;

const RightAddon = styled.div`
    margin-left: auto;
`;
const LeftAddon = styled.div`
    margin-right: 1rem;
`;

const HeaderInnerContent = styled.div`
    flex: 1;
`;

const HeaderNavigation = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 55px;
    @media (min-width: 920px) {
        display: none;
    }
`;

export const Header = ({children, rightAddon, leftAddon, className, role}) => {
    const {toggle, isOpen} = useContext(LayoutContext);
    const onOpenMenu = (e) => {
        // We need to prevent the bubbling of the event upwards otherwise
        // container will catch this click and immediately close the drawer.
        e.stopPropagation();
        toggle(true);
    };
    return (
        <HeaderContainer data-test-id="header-container" className={className} role={role}>
            <HeaderNavigation>
                <Button
                    iconOnly
                    clear
                    onClick={onOpenMenu}
                    id="main-menu-toggle"
                    aria-controls="main-menu"
                    aria-label={isOpen ? "Close main menu" : "Open main menu"}
                >
                    <MenuIcon aria-hidden="true" display="block" />
                </Button>
            </HeaderNavigation>

            <HeaderContent>
                {leftAddon && <LeftAddon>{leftAddon}</LeftAddon>}
                <HeaderInnerContent>{children}</HeaderInnerContent>
                {rightAddon && <RightAddon data-test-id="right-addon">{rightAddon}</RightAddon>}
            </HeaderContent>
        </HeaderContainer>
    );
};

export const LandingPageHeader = ({children, rightAddon, leftAddon, className}) => {
    return (
        <HeaderContainer className={className} paddingLeft="32px">
            <HeaderContent>
                {leftAddon && <LeftAddon>{leftAddon}</LeftAddon>}
                <HeaderInnerContent>{children}</HeaderInnerContent>
                {rightAddon && <RightAddon>{rightAddon}</RightAddon>}
            </HeaderContent>
        </HeaderContainer>
    );
};
